import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CircleDot, X } from "lucide-react";

const ageMinutes = (iso, now) => {
  if (!iso) return null;
  return (new Date(now).getTime() - new Date(iso).getTime()) / 60000;
};

const fmtAge = (mins) => {
  if (mins == null) return "never";
  if (mins < 1) return "just now";
  if (mins < 60) return `${Math.round(mins)} min ago`;
  const h = mins / 60;
  if (h < 24) return `${h.toFixed(1)} h ago`;
  return `${Math.round(h / 24)} d ago`;
};

const tone = (mins) => {
  if (mins == null) return { color: "bg-red-500", text: "text-red-600 dark:text-red-400", label: "no data" };
  if (mins < 15) return { color: "bg-emerald-500", text: "text-emerald-600 dark:text-emerald-400", label: "fresh" };
  if (mins < 60) return { color: "bg-amber-500", text: "text-amber-600 dark:text-amber-400", label: "aging" };
  return { color: "bg-red-500", text: "text-red-600 dark:text-red-400", label: "stale" };
};

export const SyncHealth = () => {
  const [health, setHealth] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/tools/sync-health").then(({ data }) => setHealth(data)).catch(() => {});
  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);

  if (!health) return null;

  const qs = health.quick_sync || {};
  const amz = health.amazon || {};
  const mins = ageMinutes(qs.last_sync, health.now);
  const t = tone(mins);
  const running = qs.running;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="sync-health-widget"
        className="inline-flex items-center gap-2.5 rounded-full border border-border bg-card px-3.5 py-1.5 shadow-sm hover:shadow-md hover:border-foreground/20 transition-all"
        title="Click for full sync details"
      >
        <span className="relative flex h-2.5 w-2.5">
          {running && <span className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 ${t.color}`} />}
          <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${t.color}`} data-testid="sync-health-dot" />
        </span>
        <span className="text-xs font-semibold text-foreground">Sync</span>
        <span className={`text-xs ${t.text}`} data-testid="sync-health-age">
          {running ? "refreshing…" : fmtAge(mins)}
        </span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="sync-health-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CircleDot className={`h-4 w-4 ${t.text}`} aria-hidden="true" />
              Sync health · {t.label}
            </DialogTitle>
            <DialogDescription>
              Hourly `quick_sync` keeps today &amp; yesterday sales fresh. Nightly full sync backfills everything.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 text-sm">
            <div className="rounded-lg border border-border p-4 bg-muted/30">
              <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Hourly quick sync (last 36 h)</p>
              <div className="mt-2 grid grid-cols-2 gap-3">
                <StatRow k="Last completed" v={qs.last_sync ? new Date(qs.last_sync).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "never"} />
                <StatRow k="Age" v={fmtAge(mins)} tone={t.text} />
                <StatRow k="Currently running" v={running ? "yes" : "no"} />
                <StatRow k="Orders in last cycle" v={qs.orders != null ? qs.orders.toLocaleString("en-IN") : "—"} />
                <StatRow k="Started at" v={qs.started_at ? new Date(qs.started_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"} />
                <StatRow k="Heartbeat" v={qs.heartbeat ? new Date(qs.heartbeat).toLocaleString("en-IN", { timeStyle: "short" }) : "—"} />
              </div>
              {qs.error && <p className="mt-2 text-xs text-red-600 dark:text-red-400" data-testid="sync-health-quick-error">Error: {qs.error}</p>}
            </div>

            <div className="rounded-lg border border-border p-4 bg-muted/30">
              <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Full sync (nightly / on-demand)</p>
              <div className="mt-2 grid grid-cols-2 gap-3">
                <StatRow k="Last completed" v={amz.last_sync ? new Date(amz.last_sync).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "never"} />
                <StatRow k="Phase" v={amz.phase || "idle"} />
                <StatRow k="Currently running" v={amz.running ? "yes" : "no"} />
                <StatRow k="Finance backfilled" v={amz.finance_backfill_done ? "yes" : "no"} />
              </div>
              {amz.error && <p className="mt-2 text-xs text-red-600 dark:text-red-400">Error: {amz.error}</p>}
            </div>

            <a
              href="/integrations"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:underline"
              data-testid="sync-health-full-monitor-link"
            >
              Open full sync monitor →
            </a>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const StatRow = ({ k, v, tone: t }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{k}</p>
    <p className={`text-sm font-semibold mt-0.5 ${t || ""}`}>{v}</p>
  </div>
);
